import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
  toggle: () => void;
}

const STORAGE_KEY = 'sb-theme-preference';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'dark' || raw === 'light' || raw === 'system') return raw;
  } catch {
    // localStorage might be unavailable (privacy mode, etc.) — fall through.
  }
  return 'system';
}

function readSystemPreference(): ResolvedTheme {
  try {
    return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function resolve(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? readSystemPreference() : preference;
}

function applyToDocument(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-bs-theme', resolved);
  root.setAttribute('data-theme', resolved);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredPreference()));
  const isInitialMount = useRef(true);

  useEffect(() => {
    const next = resolve(preference);
    setResolved(next);
    applyToDocument(next);

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Persistence is best-effort; silently ignore.
    }
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(MEDIA_QUERY);
    } catch {
      return;
    }
    const listener = () => {
      const next = mql.matches ? 'dark' : 'light';
      setResolved(next);
      applyToDocument(next);
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', listener);
      return () => mql.removeEventListener('change', listener);
    }
    mql.addListener(listener);
    return () => mql.removeListener(listener);
  }, [preference]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
  }, []);

  const toggle = useCallback(() => {
    setPreferenceState((prev) => {
      const current = resolve(prev);
      return current === 'dark' ? 'light' : 'dark';
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, toggle }),
    [preference, resolved, setPreference, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error('useTheme yalnızca ThemeProvider içinde kullanılabilir.');
  }
  return ctx;
}
